import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { AccountDisplayWithMembership } from '~/components/ui/AccountDisplay';
import { AccountSelectMenuContent, AccountSelectMenuItem } from '~/components/ui/AccountSelectMenu';
import { Avatar } from '~/components/ui/Avatar';
import { UserAvatar } from '~/components/ui/UserAvatar';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import type { PricingWorkspace } from '~/lib/stores/pricing/types';
import { workspacesStore } from '~/lib/stores/pricing/workspaces';
import { AccountSelectedTrigger, AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { dropdownAnimationVariants } from '~/utils/animationVariants';

export const WorkspaceSelector = ({
  onSelectWorkspace,
}: {
  onSelectWorkspace?: (workspace: PricingWorkspace) => void;
}) => {
  const workspaces = useStore(workspacesStore.workspaces);
  const selectedWorkspace = useStore(workspacesStore.selectedWorkspace);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const onToggleDropdown = useCallback(() => {
    setIsDropdownVisible((value) => !value);
  }, []);

  const onSelect = (workspace: PricingWorkspace) => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.AccountSelected,
        properties: {
          from: AccountSelectedTrigger.PricingPage,
        },
      },
    });

    workspacesStore.selectWorkspace(workspace);

    if (onSelectWorkspace) {
      onSelectWorkspace(workspace);
    }
  };

  const onCreateTeam = () => {
    teamPricingPlansStore.startCreateTeamFlow();
  };

  const disabled = workspaces.length === 1 && workspaces[0].type === 'free';

  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-bolt-ds-textTertiary">Current workspace:</span>

        <div className="text-bolt-ds-textPrimary flex flex-wrap gap-1">
          <DropdownMenu.Root open={isDropdownVisible} onOpenChange={onToggleDropdown}>
            <DropdownMenu.Trigger asChild disabled={disabled}>
              <button className="outline-none bg-transparent flex max-w-80 overflow-hidden items-center gap-2 font-semibold justify-end md:justify-start">
                {selectedWorkspace.account && (
                  <Avatar
                    user={{ avatar: selectedWorkspace.account.avatar, name: selectedWorkspace.account.accountName }}
                  />
                )}
                <span className="truncate">{selectedWorkspace.label}</span>
                {selectedWorkspace.type !== 'free' && (
                  <div className={`shrink-0 i-ph:caret-${isDropdownVisible ? 'up' : 'down'}-bold`} />
                )}
              </button>
            </DropdownMenu.Trigger>
            <AnimatePresence>
              {isDropdownVisible && (
                <DropdownMenu.Portal>
                  <AccountSelectMenuContent sideOffset={8} asChild align="start">
                    <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                      {workspaces.map((workspace) => (
                        <AccountSelectMenuItem key={workspace.id} onClick={() => onSelect(workspace)}>
                          {workspace.account ? <AccountDisplayWithMembership {...workspace.account} /> : null}
                        </AccountSelectMenuItem>
                      ))}
                      <AccountSelectMenuItem onClick={onCreateTeam}>
                        <UserAvatar iconClassName="i-ph:users-three" />
                        <div className="text-sm font-semibold">New team</div>
                      </AccountSelectMenuItem>
                    </motion.div>
                  </AccountSelectMenuContent>
                </DropdownMenu.Portal>
              )}
            </AnimatePresence>
          </DropdownMenu.Root>
        </div>
      </div>
    </div>
  );
};
